/**
 * COMBAT MODEL — armor classes, weapon effectiveness, and the counter-play table.
 * Spec: bible 06-SHIPS-AND-WEAPONS Appendix A.
 *
 * The design goal, in one line: **no weapon is strictly better than another.**
 * Each main weapon wins decisively against one armor class and is punished
 * against another, so the drifting red/blue pod choice is a live tactical
 * decision every few seconds rather than a straight upgrade.
 */

/** Nothing an enemy fires exceeds this. The player cruises at 21.0 u/s. */
export const ENEMY_BULLET_SPEED_CAP = 26

export const enum ArmorClass {
  /** Drones, light aircraft. Dies to volume. */
  LIGHT = 0,
  /** Tanks, gunships, emplacements. Dies to focus. */
  ARMORED = 1,
  /** Bosses, capital hulls, structures. Dies to sustained penetration. */
  STRUCTURE = 2,
}

export type WeaponId = 'HAILSTORM' | 'ARCLIGHT' | 'CAUSTIC' | 'MISSILE' | 'HOMING' | 'BOMB'

/**
 * Damage multipliers, indexed [LIGHT, ARMORED, STRUCTURE].
 * Verbatim from the bible's constant block — do not retune here without
 * updating 06-SHIPS-AND-WEAPONS.
 */
export const ARMOR_RESPONSE: Record<WeaponId, readonly [number, number, number]> = {
  /** Spread cannon. Shreds swarms, wastes most of its pellets on hard targets. */
  HAILSTORM: [1.0, 0.75, 0.55],
  /** Focused lance. Punches armor and pierces; poor value against scattered fodder. */
  ARCLIGHT: [0.85, 1.0, 1.15],
  /** Lock-on tether. Excellent vs light clusters, falls off against structure. */
  CAUSTIC: [1.15, 0.9, 0.8],
  /** Dumb-fire ordnance. Built for armor; overkill and inaccurate against drones. */
  MISSILE: [0.7, 1.4, 1.25],
  /** Guided. Chases fodder beautifully, barely scratches a hull. */
  HOMING: [1.2, 0.85, 0.6],
  /** Panic button. Broadly effective, deliberately weak against structure. */
  BOMB: [1.0, 1.0, 0.65],
}

/** Per-unit armor assignment, from bible 05-BESTIARY. */
export const UNIT_ARMOR: Record<string, ArmorClass> = {
  'drn01-tick': ArmorClass.LIGHT,
  'drn02-picket': ArmorClass.LIGHT,
  'drn03-roundel': ArmorClass.LIGHT,
  'drn04-splinter': ArmorClass.LIGHT,
  'drn05-drayman': ArmorClass.ARMORED,
  'drn06-shrike': ArmorClass.LIGHT,
  'drn07-crosstalk': ArmorClass.LIGHT,
  'air01-kestrel': ArmorClass.LIGHT,
  'gnd01-guard': ArmorClass.ARMORED,
  'sta01-gunpod': ArmorClass.ARMORED,
  'battery-nine': ArmorClass.STRUCTURE,
}

/** Resolve final damage for a hit. */
export function resolveDamage(base: number, weapon: WeaponId, armor: ArmorClass): number {
  return base * ARMOR_RESPONSE[weapon][armor]
}

/**
 * The counter-play the player is meant to internalise. Surfaced in the HUD as a
 * one-word hint the first time each armor class appears, then never again —
 * the game should teach this once and then trust you.
 */
export const COUNTER_HINT: Record<ArmorClass, { best: WeaponId; worst: WeaponId; hint: string }> = {
  [ArmorClass.LIGHT]: {
    best: 'HOMING',
    worst: 'MISSILE',
    hint: 'Swarms die to spread. Save ordnance.',
  },
  [ArmorClass.ARMORED]: {
    best: 'MISSILE',
    worst: 'HAILSTORM',
    hint: 'Armor needs focus. Spread will not cut it.',
  },
  [ArmorClass.STRUCTURE]: {
    best: 'ARCLIGHT',
    worst: 'HOMING',
    hint: 'Structure needs penetration. Hold the lance on it.',
  },
}

/**
 * Effectiveness readout for UI, 0..1 normalised across the six weapons.
 * Used to drive the small chevron indicator next to the weapon name so the
 * player can see the matchup without reading a manual.
 */
export function effectiveness(weapon: WeaponId, armor: ArmorClass): number {
  const all = Object.values(ARMOR_RESPONSE).map((r) => r[armor])
  const min = Math.min(...all)
  const max = Math.max(...all)
  const v = ARMOR_RESPONSE[weapon][armor]
  return max === min ? 0.5 : (v - min) / (max - min)
}
